import moment from "moment";
import { useState } from "react";
import { Link } from "react-router";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import SimpleTable, { type ColumnDef } from "@/components/shared/simple-table";
import AppointmentStatusBadge, { APPOINTMENT_STATUS_OPTIONS } from "@/app/pmc/appointments/components/AppointmentStatusBadge";
import { useDebounce } from "@/hooks/useDebounce";
import type { FilterItem, uuid } from "@/interfaces";
import type { appointment_status } from "@/models/appointment.model";
import type { AppointmentJoin } from "@/repositories/appointments.repository";
import { useContactAppointments } from "./contact.hooks";

export const assigneeOf = (row: AppointmentJoin): string =>
  [row.assigned_organization?.display_name, row.assigned_user?.name].filter(Boolean).join(" · ") || "Unassigned";

const unitOf = (row: AppointmentJoin): string => (row.property.unit_number ? `Unit ${row.property.unit_number}` : "");

type Props = { property?: uuid; title: string; description: string; appointmentHref?: (appointment: uuid) => string };

const appointmentPage = (appointment: uuid) => `/appointments/${appointment}`;

export function ContactAppointmentsTable({ property, title, description, appointmentHref = appointmentPage }: Props) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortKey, setSortKey] = useState("date");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<appointment_status | "">("");
  const debouncedSearch = useDebounce(search, 400);
  const { value, loading, error } = useContactAppointments({ property, status, search: debouncedSearch, page, pageSize, sortKey, sortOrder });

  const sortBy = (key: string) => {
    if (sortKey === key) setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    else {
      setSortKey(key);
      setSortOrder("asc");
    }
  };

  const columns: ColumnDef<AppointmentJoin>[] = [
    {
      header: "ID",
      accessor: (row) => (
        <Link to={appointmentHref(row.id)} className="font-medium text-primary hover:underline">
          {row.id.slice(0, 8)}
        </Link>
      ),
      sortable: true,
      sortKey: "id",
      className: "w-[100px]",
    },
    {
      header: "Unit",
      accessor: (row) => (
        <div className="flex flex-col">
          <span className="font-medium">{row.property.name}</span>
          <span className="text-muted-foreground">{unitOf(row)}</span>
        </div>
      ),
      sortable: true,
      sortKey: "unit_number",
    },
    {
      header: "Service",
      accessor: (row) => (
        <div className="flex flex-col">
          <span className="font-medium">{row.service.name}</span>
          {row.service.parent_name && <span className="text-xs text-muted-foreground">{row.service.parent_name}</span>}
        </div>
      ),
      sortable: true,
      sortKey: "service",
    },
    { header: "Vendor", accessor: (row) => row.vendor.name, sortable: true, sortKey: "vendor" },
    { header: "Assigned To", accessor: assigneeOf },
    { header: "Scheduled", accessor: (row) => moment(row.date).format("MM/DD/YYYY"), sortable: true, sortKey: "date" },
    { header: "Status", accessor: (row) => <AppointmentStatusBadge status={row.status} />, sortable: true, sortKey: "status" },
  ];

  const filters: FilterItem[] = [
    {
      type: "search",
      title: "Search appointments...",
      value: search,
      onValueChange: (value: string) => {
        setPage(1);
        setSearch(value);
      },
      grid: 3,
    },
    {
      type: "select",
      title: "Status",
      value: status,
      onValueChange: (value: appointment_status | "") => {
        setPage(1);
        setStatus(value);
      },
      options: APPOINTMENT_STATUS_OPTIONS,
      grid: 2,
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {error ? (
          <p className="text-destructive py-4 text-center">{error}</p>
        ) : (
          <SimpleTable
            columns={columns}
            data={value?.data ?? []}
            totalCount={value?.count ?? 0}
            loading={loading}
            page={page}
            pageSize={pageSize}
            setPage={setPage}
            setPageSize={(size) => {
              setPage(1);
              setPageSize(size);
            }}
            sortKey={sortKey}
            sortOrder={sortOrder}
            onSortChange={sortBy}
            filters={filters}
            onReset={() => {
              setPage(1);
              setSearch("");
              setStatus("");
            }}
            container={false}
          />
        )}
      </CardContent>
    </Card>
  );
}
